import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTaskModal } from '../context/TaskModalContext';
import InfoModal from '../components/InfoModal';
import {
  listInitiatives,
  listPieces,
  getPiece,
  createPiece,
  generatePiece,
  approvePiece,
  rejectPiece,
  requestPieceChanges,
  askPieceQuestion,
  listPieceQuestions,
  getInitiativeContext,
  saveInitiativeContext,
  getContentGuidelines,
  saveContentGuidelines,
  getInitiativeTasks,
  createInitiativeTask,
  getAvailableModels,
  listContentPlatforms,
  InitiativeSummary,
  InitiativeContext,
  InitiativeTask,
  PieceSummary,
  Piece,
  PieceQuestionAnswer,
  ContentPlatform,
} from '../services/api';

// Piece types: text types (Claude generate→critic loop), a video_script (a
// script-framed text type), and image (image API). Mirrors the backend
// vocabulary in content_service.PIECE_TYPES.
const TEXT_PIECE_TYPES = ['post', 'article', 'thread', 'story', 'reel', 'ad', 'email', 'video_script'] as const;
const ALL_PIECE_TYPES = [...TEXT_PIECE_TYPES, 'image'] as const;
const isImageType = (t?: string): boolean => (t || '').toLowerCase() === 'image';

// Roles the AI can address a question to (mirrors the team-member list).
const QUESTION_ROLES = ['joshua', 'subham', 'aazmeer', 'thadeus', 'zee'] as const;

// Sentinel value for the "Other (specify)" option in the platform picker.
const OTHER_PLATFORM = '__other__';

const PIECE_TYPE_LABEL: Record<string, string> = {
  post: 'Post', article: 'Article', thread: 'Thread', story: 'Story',
  reel: 'Reel', ad: 'Ad', email: 'Email', video_script: 'Video script', image: 'Image',
};
const pieceTypeLabel = (t?: string): string => (t && PIECE_TYPE_LABEL[t]) || t || 'Post';

// ============================================================================
// Initiatives page.
//
// An initiative is more than content: it has a Context (editable folder files),
// general Tasks, and a Content flow (the AI draft -> critic -> approve loop).
//
//   Left column: filterable list of initiatives (by domain).
//   Detail: three tabs — Context | Tasks | Content.
//
// Wired endpoints:
//   GET  /content/initiatives                      (now includes domain + quest_id)
//   GET/PUT /content/initiatives/{id}/context      (4 raw file strings)
//   GET/POST /content/initiatives/{id}/tasks       (general, non-content tasks)
//   GET  /content/initiatives/{id}/pieces ...      (the CONTENT flow)
// ============================================================================

const PAGE_INTRO =
  'Each initiative has its context, its tasks, and its content. Edit the context ' +
  'files, queue general tasks for your AI team, or run the content draft→critic→approve loop.';

// ---- Quest web app deep-linking -------------------------------------------
const QUEST_WEB = (process.env.REACT_APP_QUEST_WEB_URL || 'https://quest.spiritualdata.org').replace(/\/+$/, '');
const questUrl = (questId?: string | null): string =>
  questId ? `${QUEST_WEB}/quest/${encodeURIComponent(questId)}` : `${QUEST_WEB}/quest`;

const OpenInQuestLink: React.FC<{
  questId?: string | null;
  color: string;
  fontSize?: string;
  fontWeight?: number;
}> = ({ questId, color, fontSize = '13px', fontWeight = 600 }) => (
  <a
    href={questUrl(questId)}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(e) => e.stopPropagation()}
    style={{ color, fontSize, fontWeight, textDecoration: 'none', whiteSpace: 'nowrap' }}
    title="Open in the Quest web app"
  >
    Open in Quest ↗
  </a>
);

// ---- Domains + people ------------------------------------------------------
const DOMAINS = ['product', 'growth', 'impact', 'content', 'mission'] as const;
type DomainFilter = 'all' | (typeof DOMAINS)[number];

// AI team members an initiative task can be assigned to.
const TEAM_MEMBERS = ['joshua', 'subham', 'aazmeer', 'thadeus', 'zee'] as const;

// Piece statuses (content lifecycle). Unknown values fall back to neutral.
const STATUS_COLOR: Record<string, string> = {
  drafting: '#6b7280',
  generating: '#3b82f6',
  under_review: '#f59e0b',
  revising: '#f59e0b',
  ready: '#8b5cf6',
  approved: '#10b981',
  posted: '#10b981',
  rejected: '#ef4444',
  escalated: '#ef4444',
  error: '#ef4444',
  revision_requested: '#f59e0b',
};

const STATUS_LABEL: Record<string, string> = {
  drafting: 'Not generated',
  generating: 'Generating…',
  under_review: 'In review',
  revising: 'Revising',
  ready: 'Ready',
  approved: 'Approved',
  posted: 'Posted',
  rejected: 'Rejected',
  escalated: 'Escalated',
  error: 'Generation failed',
  revision_requested: 'Changes requested',
};

// Statuses that mean an async generation run is in flight: keep polling the
// piece until it leaves these. (run_initiative_piece briefly sets under_review
// while the critic runs.)
const IN_FLIGHT_STATUSES = ['generating', 'under_review', 'revising'];
const isInFlight = (status?: string): boolean =>
  !!status && IN_FLIGHT_STATUSES.includes(status);

const statusColor = (status?: string): string =>
  (status && STATUS_COLOR[status]) || '#6b7280';

const statusLabel = (status?: string): string =>
  (status && STATUS_LABEL[status]) || status || 'Unknown';

const initiativeTitle = (i: InitiativeSummary): string => i.name || i.id;

const pieceTitle = (p: PieceSummary): string => {
  const platform = p.platform ? p.platform : '';
  const type = pieceTypeLabel(p.type);
  if (platform) return `${platform} · ${type}`;
  return `${type} (${p.id})`;
};

type Tab = 'context' | 'tasks' | 'content';

// One short line of help per tab, so a new user understands each.
const TAB_HELP: Record<Tab, string> = {
  context: "The initiative's background + Content Guidelines used for all its content.",
  tasks: 'Non-content work for this initiative.',
  content: 'Create a piece, the AI drafts it and a critic reviews until it passes, then you approve before posting.',
};

const Initiatives: React.FC = () => {
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const initiativeIdParam = searchParams.get('initiative');
  const pieceIdParam = searchParams.get('piece');

  const [initiatives, setInitiatives] = useState<InitiativeSummary[]>([]);
  const [initiativesLoading, setInitiativesLoading] = useState(true);
  const [initiativesError, setInitiativesError] = useState<string | null>(null);

  const [domainFilter, setDomainFilter] = useState<DomainFilter>('all');
  const [tab, setTab] = useState<Tab>('context');

  // Available Claude models (for the Tasks tab "New task" model dropdown).
  const [availableModels, setAvailableModels] = useState<string[]>([]);

  // The FULL list of supported platforms (for the create-piece picker).
  const [allPlatforms, setAllPlatforms] = useState<ContentPlatform[]>([]);

  // ---- Content flow state (pieces) -----------------------------------------
  const [pieces, setPieces] = useState<PieceSummary[]>([]);
  const [piecesLoading, setPiecesLoading] = useState(false);
  const [piecesError, setPiecesError] = useState<string | null>(null);

  const [piece, setPiece] = useState<Piece | null>(null);
  const [pieceLoading, setPieceLoading] = useState(false);
  const [pieceError, setPieceError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [formPlatform, setFormPlatform] = useState('');
  // When the user picks "Other (specify)", formPlatform === OTHER_PLATFORM and
  // this holds the free-text platform name they typed.
  const [formCustomPlatform, setFormCustomPlatform] = useState('');
  const [formType, setFormType] = useState('post');
  const [formBrief, setFormBrief] = useState('');
  const [formImagePrompt, setFormImagePrompt] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const selectedInitiative = initiatives.find((i) => i.id === initiativeIdParam) || null;
  const platforms = selectedInitiative?.platforms || [];

  const visibleInitiatives = domainFilter === 'all'
    ? initiatives
    : initiatives.filter((i) => i.domain === domainFilter);

  // Default the platform dropdown when the selected initiative changes; also
  // reset the active tab to Context for each new initiative.
  useEffect(() => {
    setFormPlatform(platforms[0] || '');
    setFormCustomPlatform('');
    setFormType('post');
    setFormBrief('');
    setFormImagePrompt('');
    setCreateError(null);
    setTab('context');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiativeIdParam]);

  // If a piece is selected via URL, jump to the Content tab to show it.
  useEffect(() => {
    if (pieceIdParam) setTab('content');
  }, [pieceIdParam]);

  // Load initiatives on mount.
  const loadInitiatives = useCallback(async () => {
    setInitiativesLoading(true);
    setInitiativesError(null);
    try {
      setInitiatives(await listInitiatives());
    } catch (err) {
      console.error('Failed to load initiatives', err);
      setInitiativesError('Failed to load initiatives.');
    } finally {
      setInitiativesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitiatives();
  }, [loadInitiatives]);

  // Load the Claude models for the Tasks tab model selector (mirrors CreateTask).
  useEffect(() => {
    let cancelled = false;
    getAvailableModels()
      .then((models) => {
        if (!cancelled) setAvailableModels(models);
      })
      .catch((err) => console.error('Failed to load available models', err));
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the FULL platform list (for the create-piece picker) on mount.
  useEffect(() => {
    let cancelled = false;
    listContentPlatforms()
      .then((p) => { if (!cancelled) setAllPlatforms(p); })
      .catch((err) => console.error('Failed to load platforms', err));
    return () => { cancelled = true; };
  }, []);

  // ---- Content: pieces -----------------------------------------------------
  const loadPieces = useCallback(async (initiativeId: string) => {
    setPiecesLoading(true);
    setPiecesError(null);
    try {
      setPieces(await listPieces(initiativeId));
    } catch (err) {
      console.error('Failed to load pieces', err);
      setPiecesError('Failed to load pieces.');
      setPieces([]);
    } finally {
      setPiecesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!initiativeIdParam) {
      setPieces([]);
      return;
    }
    loadPieces(initiativeIdParam);
  }, [initiativeIdParam, loadPieces]);

  const loadPiece = useCallback(async (initiativeId: string, pieceId: string) => {
    setPieceLoading(true);
    setPieceError(null);
    try {
      setPiece(await getPiece(initiativeId, pieceId));
    } catch (err) {
      console.error('Failed to load piece', err);
      setPieceError('Failed to load piece.');
      setPiece(null);
    } finally {
      setPieceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!initiativeIdParam || !pieceIdParam) {
      setPiece(null);
      return;
    }
    loadPiece(initiativeIdParam, pieceIdParam);
  }, [initiativeIdParam, pieceIdParam, loadPiece]);

  // While a piece is generating (async), poll it every 3s until it leaves the
  // in-flight statuses, then the draft + critic trail render automatically.
  useEffect(() => {
    if (!initiativeIdParam || !pieceIdParam || !piece) return;
    if (!isInFlight(piece.status)) return;
    const id = setInterval(() => {
      getPiece(initiativeIdParam, pieceIdParam)
        .then((p) => setPiece(p))
        .catch((err) => console.error('Failed to poll piece', err));
    }, 3000);
    return () => clearInterval(id);
  }, [initiativeIdParam, pieceIdParam, piece]);

  const handleSelectInitiative = useCallback((initiative: InitiativeSummary) => {
    setSearchParams({ initiative: initiative.id });
  }, [setSearchParams]);

  const handleSelectPiece = useCallback((p: PieceSummary) => {
    if (!initiativeIdParam) return;
    setSearchParams({ initiative: initiativeIdParam, piece: p.id });
  }, [setSearchParams, initiativeIdParam]);

  const refreshAfterAction = useCallback(async () => {
    if (!initiativeIdParam) return;
    await loadPieces(initiativeIdParam);
    if (pieceIdParam) {
      await loadPiece(initiativeIdParam, pieceIdParam);
    }
  }, [initiativeIdParam, pieceIdParam, loadPieces, loadPiece]);

  const handleApprove = useCallback(async () => {
    if (!initiativeIdParam || !pieceIdParam) return;
    setActionBusy(true);
    setPieceError(null);
    try {
      await approvePiece(initiativeIdParam, pieceIdParam);
      await refreshAfterAction();
    } catch (err) {
      console.error('Failed to approve piece', err);
      setPieceError('Failed to approve piece.');
    } finally {
      setActionBusy(false);
    }
  }, [initiativeIdParam, pieceIdParam, refreshAfterAction]);

  const handleReject = useCallback(async () => {
    if (!initiativeIdParam || !pieceIdParam) return;
    const reason = window.prompt('Reason for rejection (optional):') || '';
    setActionBusy(true);
    setPieceError(null);
    try {
      await rejectPiece(initiativeIdParam, pieceIdParam, reason);
      await refreshAfterAction();
    } catch (err) {
      console.error('Failed to reject piece', err);
      setPieceError('Failed to reject piece.');
    } finally {
      setActionBusy(false);
    }
  }, [initiativeIdParam, pieceIdParam, refreshAfterAction]);

  const handleCreatePiece = useCallback(async () => {
    if (!initiativeIdParam) return;
    // Resolve the platform: a picked one, or the free-text "Other" value.
    const resolvedPlatform = formPlatform === OTHER_PLATFORM
      ? formCustomPlatform.trim()
      : formPlatform;
    if (!resolvedPlatform) {
      setCreateError(formPlatform === OTHER_PLATFORM
        ? 'Type the platform name.'
        : 'Choose a platform.');
      return;
    }
    const type = formType.trim() || 'post';
    if (isImageType(type)) {
      if (!formImagePrompt.trim()) {
        setCreateError('Describe the image to generate (the image prompt is required).');
        return;
      }
    } else if (!formBrief.trim()) {
      setCreateError('Tell the AI what to create (the brief is required).');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const created = await createPiece(
        initiativeIdParam,
        resolvedPlatform,
        type,
        formBrief.trim(),
        formImagePrompt.trim(),
      );
      await loadPieces(initiativeIdParam);
      setFormBrief('');
      setFormImagePrompt('');
      if (created && created.id) {
        setSearchParams({ initiative: initiativeIdParam, piece: created.id });
        // Submitting the idea IS the go signal — auto-start generation right away,
        // no separate "Generate" click required. (The Generate button remains for
        // retry / regenerate / "request changes then regenerate".)
        setGenerating(true);
        try {
          await generatePiece(initiativeIdParam, created.id);
        } catch (genErr: any) {
          const detail = genErr?.response?.data?.detail;
          if (genErr?.response?.status === 409 && typeof detail === 'string') {
            setPieceError(detail); // e.g. image API not configured — points to Dependencies
          } else {
            console.error('Auto-generate failed', genErr);
            setPieceError('Created, but generation didn’t start — press Generate to retry.');
          }
        } finally {
          setGenerating(false);
          await loadPieces(initiativeIdParam);
        }
      }
    } catch (err) {
      console.error('Failed to create piece', err);
      setCreateError('Failed to create piece.');
    } finally {
      setCreating(false);
    }
  }, [initiativeIdParam, formPlatform, formCustomPlatform, formType, formBrief, formImagePrompt, loadPieces, setSearchParams]);

  const handleRequestChanges = useCallback(async (notes: string) => {
    if (!initiativeIdParam || !pieceIdParam) return;
    setActionBusy(true);
    setPieceError(null);
    try {
      await requestPieceChanges(initiativeIdParam, pieceIdParam, notes);
      await refreshAfterAction();
    } catch (err) {
      console.error('Failed to request changes', err);
      setPieceError('Failed to request changes.');
    } finally {
      setActionBusy(false);
    }
  }, [initiativeIdParam, pieceIdParam, refreshAfterAction]);

  const handleAskQuestion = useCallback(async (question: string, ownerRole: string) => {
    if (!initiativeIdParam || !pieceIdParam) return;
    setActionBusy(true);
    setPieceError(null);
    try {
      await askPieceQuestion(initiativeIdParam, pieceIdParam, question, ownerRole);
      await refreshAfterAction();
    } catch (err) {
      console.error('Failed to ask question', err);
      setPieceError('Failed to create the question.');
    } finally {
      setActionBusy(false);
    }
  }, [initiativeIdParam, pieceIdParam, refreshAfterAction]);

  const handleGenerate = useCallback(async () => {
    if (!initiativeIdParam || !pieceIdParam) return;
    setGenerating(true);
    setPieceError(null);
    try {
      await generatePiece(initiativeIdParam, pieceIdParam);
      await refreshAfterAction();
    } catch (err: any) {
      console.error('Failed to generate piece', err);
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && typeof detail === 'string') {
        // Image API not configured — point the user to Dependencies.
        setPieceError(detail);
      } else {
        setPieceError('Failed to generate piece.');
      }
    } finally {
      setGenerating(false);
    }
  }, [initiativeIdParam, pieceIdParam, refreshAfterAction]);

  return (
    <div className="container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px', overflowX: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {selectedInitiative && (
            <button
              className="mobile-back-btn"
              onClick={() => setSearchParams({})}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.surface,
                color: theme.text,
                cursor: 'pointer',
                fontSize: '14px',
                display: 'none',
              }}
            >
              ← Back
            </button>
          )}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h1 style={{ color: theme.text, margin: 0 }}>Initiatives / Content</h1>
              <InfoModal
                title="Initiatives / Content"
                body={[
                  "An initiative bundles a Context (its background + Content Guidelines), general Tasks for your AI team, and a Content flow.",
                  "In Content, you brief a piece, the AI drafts it and a critic reviews until it passes, then you approve before posting.",
                  "Edit the Content Guidelines once and they apply to every piece generated for the initiative.",
                ]}
              />
            </div>
            <p style={{ color: theme.textSecondary, margin: '4px 0 0', fontSize: '14px', maxWidth: '760px' }}>
              {PAGE_INTRO}
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .mobile-back-btn {
            display: block !important;
          }
          .initiatives-list-panel {
            display: ${selectedInitiative ? 'none' : 'block'} !important;
          }
          .initiatives-detail-panel {
            display: ${selectedInitiative ? 'block' : 'none'} !important;
          }
        }
      `}</style>

      <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px' }}>
        {/* List of initiatives + domain filter */}
        <div className="initiatives-list-panel">
          <DomainFilterControl value={domainFilter} onChange={setDomainFilter} />

          {initiativesLoading ? (
            <PanelMessage>Loading initiatives…</PanelMessage>
          ) : initiativesError ? (
            <PanelMessage tone="error">{initiativesError}</PanelMessage>
          ) : initiatives.length === 0 ? (
            <PanelMessage>No initiatives yet.</PanelMessage>
          ) : visibleInitiatives.length === 0 ? (
            <PanelMessage>No initiatives in the {domainFilter} domain.</PanelMessage>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {visibleInitiatives.map((initiative) => (
                <div
                  key={initiative.id}
                  onClick={() => handleSelectInitiative(initiative)}
                  style={{
                    padding: '12px 14px',
                    backgroundColor: selectedInitiative?.id === initiative.id ? theme.surfaceHover : theme.surface,
                    borderRadius: '8px',
                    border: `1px solid ${selectedInitiative?.id === initiative.id ? theme.primary : theme.border}`,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                    <strong style={{ color: theme.text, fontSize: '14px' }}>{initiativeTitle(initiative)}</strong>
                    <OpenInQuestLink questId={initiative.quest_id} color={theme.primary} fontSize="12px" />
                  </div>
                  <div style={{ color: theme.textSecondary, fontSize: '12px', marginTop: '4px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <span>{initiative.status ? statusLabel(initiative.status) : 'No status'}</span>
                    {initiative.domain && <DomainBadge domain={initiative.domain} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail: tabbed (Context | Tasks | Content) */}
        <div className="initiatives-detail-panel" style={{ minWidth: 0 }}>
          {!selectedInitiative ? (
            <PanelMessage>Select an initiative to view its context, tasks, and content.</PanelMessage>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Detail header: name + Open in Quest + tabs */}
              <div style={{
                backgroundColor: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                padding: '20px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  <div>
                    <h2 style={{ color: theme.text, margin: 0, fontSize: '18px' }}>
                      {initiativeTitle(selectedInitiative)}
                    </h2>
                    <div style={{ color: theme.textSecondary, fontSize: '12px', marginTop: '4px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span>{selectedInitiative.status ? statusLabel(selectedInitiative.status) : 'No status'}</span>
                      {selectedInitiative.domain && <DomainBadge domain={selectedInitiative.domain} />}
                    </div>
                  </div>
                  <OpenInQuestLink questId={selectedInitiative.quest_id} color={theme.primary} fontSize="14px" />
                </div>

                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  <TabButton id="context" active={tab === 'context'} onClick={setTab}>Context</TabButton>
                  <TabButton id="tasks" active={tab === 'tasks'} onClick={setTab}>Tasks</TabButton>
                  <TabButton id="content" active={tab === 'content'} onClick={setTab}>Content</TabButton>
                </div>
                <p style={{ color: theme.textSecondary, fontSize: '12px', margin: '10px 0 0' }}>
                  {TAB_HELP[tab]}
                </p>
              </div>

              {tab === 'context' && (
                <ContextTab initiativeId={selectedInitiative.id} />
              )}

              {tab === 'tasks' && (
                <TasksTab initiativeId={selectedInitiative.id} availableModels={availableModels} />
              )}

              {tab === 'content' && (
                <ContentTab
                  selectedInitiative={selectedInitiative}
                  platforms={platforms}
                  allPlatforms={allPlatforms}
                  pieces={pieces}
                  piecesLoading={piecesLoading}
                  piecesError={piecesError}
                  piece={piece}
                  pieceLoading={pieceLoading}
                  pieceError={pieceError}
                  pieceIdParam={pieceIdParam}
                  actionBusy={actionBusy}
                  generating={generating}
                  formPlatform={formPlatform}
                  formCustomPlatform={formCustomPlatform}
                  formType={formType}
                  formBrief={formBrief}
                  formImagePrompt={formImagePrompt}
                  creating={creating}
                  createError={createError}
                  onPlatformChange={setFormPlatform}
                  onCustomPlatformChange={setFormCustomPlatform}
                  onTypeChange={setFormType}
                  onBriefChange={setFormBrief}
                  onImagePromptChange={setFormImagePrompt}
                  onCreatePiece={handleCreatePiece}
                  onSelectPiece={handleSelectPiece}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onGenerate={handleGenerate}
                  onRequestChanges={handleRequestChanges}
                  onAskQuestion={handleAskQuestion}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Small shared UI -------------------------------------------------------

const PanelMessage: React.FC<{ children: React.ReactNode; tone?: 'error' }> = ({ children, tone }) => {
  const { theme } = useTheme();
  return (
    <div style={{
      padding: '32px',
      textAlign: 'center',
      color: tone === 'error' ? '#ef4444' : theme.textSecondary,
      backgroundColor: theme.surface,
      borderRadius: '8px',
      border: `1px solid ${tone === 'error' ? '#ef4444' : theme.border}`,
    }}>
      <p style={{ margin: 0 }}>{children}</p>
    </div>
  );
};

const DomainFilterControl: React.FC<{
  value: DomainFilter;
  onChange: (v: DomainFilter) => void;
}> = ({ value, onChange }) => {
  const { theme } = useTheme();
  const options: DomainFilter[] = ['all', ...DOMAINS];
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            style={{
              padding: '5px 12px',
              borderRadius: '999px',
              border: `1px solid ${active ? theme.primary : theme.border}`,
              backgroundColor: active ? theme.primary : theme.surface,
              color: active ? '#fff' : theme.text,
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'capitalize',
            }}
          >
            {opt === 'all' ? 'All' : opt}
          </button>
        );
      })}
    </div>
  );
};

const DomainBadge: React.FC<{ domain: string }> = ({ domain }) => {
  const { theme } = useTheme();
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: '999px',
      backgroundColor: theme.background,
      border: `1px solid ${theme.border}`,
      color: theme.textSecondary,
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'capitalize',
    }}>
      {domain}
    </span>
  );
};

const TabButton: React.FC<{
  id: Tab;
  active: boolean;
  onClick: (id: Tab) => void;
  children: React.ReactNode;
}> = ({ id, active, onClick, children }) => {
  const { theme } = useTheme();
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      style={{
        padding: '8px 16px',
        borderRadius: '6px',
        border: `1px solid ${active ? theme.primary : theme.border}`,
        backgroundColor: active ? theme.primary : theme.surface,
        color: active ? '#fff' : theme.text,
        fontWeight: 600,
        fontSize: '13px',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const { theme } = useTheme();
  return (
    <div style={{ marginTop: '16px' }}>
      <h3 style={{ color: theme.text, fontSize: '14px', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {title}
      </h3>
      {children}
    </div>
  );
};

const StatusBadge: React.FC<{ status?: string }> = ({ status }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '12px',
      backgroundColor: statusColor(status) + '22',
      color: statusColor(status),
      fontSize: '12px',
      fontWeight: 600,
    }}
  >
    {statusLabel(status)}
  </span>
);

// ============================================================================
// TAB 1: Context — edit the 4 raw initiative files (mirrors Feature context).
// ============================================================================

// The four editable context files, in display order.
// standards.md is the Content Guidelines; it is edited via the dedicated
// Content Guidelines editor at the top of this tab, so it is omitted here to
// avoid two editors fighting over the same file.
const CONTEXT_FIELDS: Array<{ key: keyof InitiativeContext; label: string }> = [
  { key: 'initiative_yaml', label: 'initiative.yaml' },
  { key: 'context_md', label: 'context.md' },
  { key: 'references_yaml', label: 'references.yaml' },
];

// Content Guidelines editor — the initiative's voice/editorial standards
// (backed by standards.md). Clearly labeled so it's obvious these apply to ALL
// content for the initiative. Reused on both the Context and Content tabs.
const ContentGuidelinesEditor: React.FC<{ initiativeId: string }> = ({ initiativeId }) => {
  const { theme } = useTheme();
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'error'; msg: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setFeedback(null);
    getContentGuidelines(initiativeId)
      .then((g) => { if (!cancelled) setValue(g ?? ''); })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load content guidelines', err);
        setLoadError('Failed to load Content Guidelines.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [initiativeId]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const saved = await saveContentGuidelines(initiativeId, value);
      setValue(saved ?? '');
      setFeedback({ kind: 'ok', msg: 'Saved. These apply to all content for this initiative.' });
    } catch (err) {
      console.error('Failed to save content guidelines', err);
      setFeedback({ kind: 'error', msg: 'Save failed. Please try again.' });
    } finally {
      setSaving(false);
    }
  }, [initiativeId, value]);

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.surface,
    border: `2px solid ${theme.primary}`,
    borderRadius: '8px',
    padding: '20px',
  };

  return (
    <div style={cardStyle}>
      <h3 style={{ color: theme.text, margin: '0 0 4px', fontSize: '16px' }}>Content Guidelines</h3>
      <p style={{ color: theme.textSecondary, margin: '0 0 12px', fontSize: '13px' }}>
        The voice and editorial rules for this initiative. These are sent to the AI on
        <strong> every</strong> piece it generates here — and the AI critic scores drafts against them.
      </p>
      {loading ? (
        <div style={{ color: theme.textSecondary, fontSize: '14px' }}>Loading guidelines…</div>
      ) : loadError ? (
        <div style={{ color: '#ef4444', fontSize: '14px' }}>{loadError}</div>
      ) : (
        <>
          <textarea
            value={value}
            spellCheck={false}
            onChange={(e) => { setValue(e.target.value); setFeedback(null); }}
            aria-label="Content Guidelines editor"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '12px',
              borderRadius: '6px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.background,
              color: theme.text,
              fontSize: '13px',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              lineHeight: 1.5,
              minHeight: '180px',
              resize: 'vertical',
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '8px 18px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: theme.primary,
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: saving ? 'default' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save Content Guidelines'}
            </button>
            {feedback && (
              <span style={{ fontSize: '13px', color: feedback.kind === 'ok' ? theme.success : '#ef4444' }}>
                {feedback.msg}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const ContextTab: React.FC<{ initiativeId: string }> = ({ initiativeId }) => {
  const { theme } = useTheme();
  const [ctx, setCtx] = useState<InitiativeContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<keyof InitiativeContext | null>(null);
  const [feedback, setFeedback] = useState<{ key: keyof InitiativeContext; kind: 'ok' | 'error'; msg: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setFeedback(null);
    getInitiativeContext(initiativeId)
      .then((data) => {
        if (cancelled) return;
        // Normalize so every textarea is a string even if a field is missing.
        setCtx({
          initiative_yaml: data.initiative_yaml ?? '',
          context_md: data.context_md ?? '',
          standards_md: data.standards_md ?? '',
          references_yaml: data.references_yaml ?? '',
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load initiative context', err);
        setLoadError('Failed to load initiative context.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [initiativeId]);

  const handleSave = useCallback(async (key: keyof InitiativeContext) => {
    if (!ctx) return;
    setSavingKey(key);
    setFeedback(null);
    try {
      const saved = await saveInitiativeContext(initiativeId, { [key]: ctx[key] });
      // Merge any echoed-back values so the editor reflects what's on disk.
      setCtx((prev) => (prev ? { ...prev, ...saved } : prev));
      setFeedback({ key, kind: 'ok', msg: 'Saved.' });
    } catch (err) {
      console.error('Failed to save initiative context', err);
      setFeedback({ key, kind: 'error', msg: 'Save failed. Please try again.' });
    } finally {
      setSavingKey(null);
    }
  }, [ctx, initiativeId]);

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    padding: '20px',
  };

  if (loading) return <PanelMessage>Loading context…</PanelMessage>;
  if (loadError) return <PanelMessage tone="error">{loadError}</PanelMessage>;
  if (!ctx) return <PanelMessage>No context available.</PanelMessage>;

  const textareaStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px',
    borderRadius: '6px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.background,
    color: theme.text,
    fontSize: '13px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    lineHeight: 1.5,
    minHeight: '200px',
    resize: 'vertical',
  };

  return (
   <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    <ContentGuidelinesEditor initiativeId={initiativeId} />
    <div style={cardStyle}>
      <p style={{ color: theme.textSecondary, margin: '0 0 16px', fontSize: '13px' }}>
        These are the raw files that define this initiative. Edit any file and save it on its own.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {CONTEXT_FIELDS.map(({ key, label }) => {
          const saving = savingKey === key;
          return (
            <div key={key}>
              <label style={{
                display: 'block',
                color: theme.text,
                fontSize: '13px',
                fontWeight: 600,
                marginBottom: '6px',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}>
                {label}
              </label>
              <textarea
                value={ctx[key]}
                spellCheck={false}
                onChange={(e) => {
                  const v = e.target.value;
                  setCtx((prev) => (prev ? { ...prev, [key]: v } : prev));
                  setFeedback((f) => (f && f.key === key ? null : f));
                }}
                style={textareaStyle}
                aria-label={`${label} editor`}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => handleSave(key)}
                  disabled={saving}
                  style={{
                    padding: '8px 18px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: theme.primary,
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: saving ? 'default' : 'pointer',
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  {saving ? 'Saving…' : `Save ${label}`}
                </button>
                {feedback && feedback.key === key && (
                  <span style={{ fontSize: '13px', color: feedback.kind === 'ok' ? theme.success : '#ef4444' }}>
                    {feedback.msg}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
   </div>
  );
};

// ============================================================================
// TAB 2: Tasks — general (non-content) tasks for the initiative.
// ============================================================================

const taskStatusLabel = (status?: string): string => {
  if (!status) return 'Unknown';
  return status.replace(/_/g, ' ');
};

const TasksTab: React.FC<{ initiativeId: string; availableModels: string[] }> = ({ initiativeId, availableModels }) => {
  const { theme } = useTheme();
  const [tasks, setTasks] = useState<InitiativeTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [member, setMember] = useState<string>(TEAM_MEMBERS[0]);
  const [model, setModel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setTasks(await getInitiativeTasks(initiativeId));
    } catch (err) {
      console.error('Failed to load initiative tasks', err);
      setLoadError('Failed to load tasks.');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [initiativeId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = useCallback(async () => {
    if (!title.trim() || !description.trim()) {
      setFormError('Give the task a title and a description.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      await createInitiativeTask(initiativeId, {
        title: title.trim(),
        description: description.trim(),
        ai_team_member: member,
        model: model || undefined,
      });
      setTitle('');
      setDescription('');
      await load();
    } catch (err) {
      console.error('Failed to create initiative task', err);
      setFormError('Failed to create task. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [initiativeId, title, description, member, model, load]);

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: '8px',
    padding: '20px',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px',
    borderRadius: '6px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.background,
    color: theme.text,
    fontSize: '13px',
  };

  const labelStyle: React.CSSProperties = {
    color: theme.textSecondary,
    fontSize: '12px',
    fontWeight: 600,
    marginBottom: '4px',
    display: 'block',
  };

  return (
    <div style={cardStyle}>
      <p style={{ color: theme.textSecondary, margin: '0 0 16px', fontSize: '13px' }}>
        General tasks for this initiative, separate from content. Queue a task for an AI team member.
      </p>

      {/* New task form */}
      <div style={{
        backgroundColor: theme.background,
        border: `1px solid ${theme.border}`,
        borderRadius: '6px',
        padding: '14px',
        marginBottom: '16px',
      }}>
        <h3 style={{ color: theme.text, fontSize: '14px', margin: '0 0 10px' }}>New task</h3>
        <div style={{ marginBottom: '10px' }}>
          <label style={labelStyle}>Title</label>
          <input
            type="text"
            value={title}
            disabled={submitting}
            placeholder="Brief summary of the task"
            onChange={(e) => setTitle(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div style={{ marginBottom: '10px' }}>
          <label style={labelStyle}>Description</label>
          <textarea
            value={description}
            disabled={submitting}
            placeholder="What needs to be done?"
            rows={3}
            onChange={(e) => setDescription(e.target.value)}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
          <div style={{ flex: '1 1 160px', minWidth: 0 }}>
            <label style={labelStyle}>AI team member</label>
            <select
              value={member}
              disabled={submitting}
              onChange={(e) => setMember(e.target.value)}
              style={{ ...inputStyle, cursor: submitting ? 'not-allowed' : 'pointer', textTransform: 'capitalize' }}
            >
              {TEAM_MEMBERS.map((m) => (
                <option key={m} value={m} style={{ textTransform: 'capitalize' }}>{m}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 160px', minWidth: 0 }}>
            <label style={labelStyle}>Model (optional)</label>
            <select
              value={model}
              disabled={submitting}
              onChange={(e) => setModel(e.target.value)}
              style={{ ...inputStyle, cursor: submitting ? 'not-allowed' : 'pointer' }}
            >
              <option value="">Default model</option>
              {availableModels.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        </div>
        {formError && (
          <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '10px' }}>{formError}</div>
        )}
        <button
          onClick={handleCreate}
          disabled={submitting}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: theme.primary,
            color: '#fff',
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.6 : 1,
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          {submitting ? 'Creating…' : 'Create task'}
        </button>
      </div>

      {/* Tasks list */}
      {loading ? (
        <div style={{ color: theme.textSecondary, fontSize: '14px' }}>Loading tasks…</div>
      ) : loadError ? (
        <div style={{ color: '#ef4444', fontSize: '14px' }}>{loadError}</div>
      ) : tasks.length === 0 ? (
        <div style={{ color: theme.textSecondary, fontSize: '14px' }}>
          No tasks yet. Create one above to queue work for an AI team member.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {tasks.map((t, idx) => (
            <div
              key={t.id ?? idx}
              style={{
                padding: '12px',
                backgroundColor: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ color: theme.text, fontSize: '14px', fontWeight: 600 }}>
                {t.title || '(untitled task)'}
              </span>
              <span style={{
                padding: '2px 10px',
                borderRadius: '999px',
                backgroundColor: theme.surface,
                border: `1px solid ${theme.border}`,
                color: theme.textSecondary,
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'capitalize',
                whiteSpace: 'nowrap',
              }}>
                {taskStatusLabel(t.status)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TAB 3: Content — the existing content flow (create piece, generate, approve).
// ============================================================================

interface NewPieceFormProps {
  platforms: string[];            // the initiative's declared subset (pre-selected)
  allPlatforms: ContentPlatform[]; // the FULL supported list to choose from
  platform: string;
  customPlatform: string;
  pieceType: string;
  brief: string;
  imagePrompt: string;
  busy: boolean;
  error: string | null;
  onPlatformChange: (value: string) => void;
  onCustomPlatformChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onBriefChange: (value: string) => void;
  onImagePromptChange: (value: string) => void;
  onSubmit: () => void;
}

const NewPieceForm: React.FC<NewPieceFormProps> = ({
  platforms,
  allPlatforms,
  platform,
  customPlatform,
  pieceType,
  brief,
  imagePrompt,
  busy,
  error,
  onPlatformChange,
  onCustomPlatformChange,
  onTypeChange,
  onBriefChange,
  onImagePromptChange,
  onSubmit,
}) => {
  const image = isImageType(pieceType);
  const { theme } = useTheme();
  const inputStyle: React.CSSProperties = {
    padding: '8px 10px',
    borderRadius: '6px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.background,
    color: theme.text,
    fontSize: '13px',
  };
  const labelStyle: React.CSSProperties = {
    color: theme.textSecondary,
    fontSize: '12px',
    fontWeight: 600,
    marginBottom: '4px',
    display: 'block',
  };
  return (
    <div style={{
      backgroundColor: theme.background,
      border: `1px solid ${theme.border}`,
      borderRadius: '6px',
      padding: '14px',
      marginBottom: '16px',
    }}>
      <h3 style={{ color: theme.text, fontSize: '14px', margin: '0 0 10px' }}>New piece</h3>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
          <label style={labelStyle}>Platform</label>
          {(() => {
            // Offer ALL supported platforms (not just the initiative's subset),
            // ordering the initiative's declared platforms first, plus "Other".
            const declared = new Set(platforms);
            const known = allPlatforms.map((p) => p.id);
            // Include any declared platform not present in the catalog as well.
            const extras = platforms.filter((p) => !known.includes(p));
            const ordered = [
              ...allPlatforms.filter((p) => declared.has(p.id)),
              ...extras.map((id) => ({ id, name: id } as ContentPlatform)),
              ...allPlatforms.filter((p) => !declared.has(p.id)),
            ];
            return (
              <select
                value={platform}
                disabled={busy}
                onChange={(e) => onPlatformChange(e.target.value)}
                style={{ ...inputStyle, width: '100%', cursor: busy ? 'not-allowed' : 'pointer' }}
              >
                {ordered.length === 0 && <option value="">(loading platforms…)</option>}
                {ordered.map((p) => (
                  <option key={p.id} value={p.id}>
                    {(p.name || p.id)}{declared.has(p.id) ? ' (this initiative)' : ''}
                  </option>
                ))}
                <option value={OTHER_PLATFORM}>Other (specify)…</option>
              </select>
            );
          })()}
          {platform === OTHER_PLATFORM && (
            <input
              type="text"
              value={customPlatform}
              disabled={busy}
              placeholder="Name the platform (e.g. Threads, Substack)"
              onChange={(e) => onCustomPlatformChange(e.target.value)}
              style={{ ...inputStyle, width: '100%', marginTop: '6px' }}
            />
          )}
          <div style={{ color: theme.textSecondary, fontSize: '11px', marginTop: '4px' }}>
            Any supported platform — the initiative's own are marked. Pick "Other" to name your own.
          </div>
        </div>
        <div style={{ flex: '1 1 140px', minWidth: 0 }}>
          <label style={labelStyle}>Type</label>
          <select
            value={pieceType}
            disabled={busy}
            onChange={(e) => onTypeChange(e.target.value)}
            style={{ ...inputStyle, width: '100%', cursor: busy ? 'not-allowed' : 'pointer' }}
          >
            {ALL_PIECE_TYPES.map((t) => (
              <option key={t} value={t}>{pieceTypeLabel(t)}</option>
            ))}
          </select>
          <div style={{ color: theme.textSecondary, fontSize: '11px', marginTop: '4px' }}>
            {image ? 'Generated via the image API.' : 'video_script produces a shootable script.'}
          </div>
        </div>
      </div>
      <div style={{ marginBottom: '10px' }}>
        <label style={labelStyle}>Brief{image ? ' (optional context)' : ''}</label>
        <textarea
          value={brief}
          disabled={busy}
          placeholder={image ? 'Optional: extra context for the image' : 'Tell the AI what to create'}
          onChange={(e) => onBriefChange(e.target.value)}
          rows={3}
          style={{ ...inputStyle, width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>
      {image && (
        <div style={{ marginBottom: '10px' }}>
          <label style={labelStyle}>Image prompt</label>
          <textarea
            value={imagePrompt}
            disabled={busy}
            placeholder="Describe the image to generate (e.g. a serene mountain at dawn, soft light)"
            onChange={(e) => onImagePromptChange(e.target.value)}
            rows={3}
            style={{ ...inputStyle, width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>
      )}
      {error && (
        <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '10px' }}>{error}</div>
      )}
      <button
        onClick={onSubmit}
        disabled={busy}
        style={{
          padding: '8px 16px',
          borderRadius: '6px',
          border: 'none',
          backgroundColor: theme.primary,
          color: '#fff',
          cursor: busy ? 'not-allowed' : 'pointer',
          opacity: busy ? 0.6 : 1,
          fontSize: '14px',
          fontWeight: 600,
        }}
      >
        {busy ? 'Generating…' : 'Generate'}
      </button>
    </div>
  );
};

interface PieceDetailProps {
  piece: Piece;
  initiativeId: string;
  busy: boolean;
  generating: boolean;
  error: string | null;
  onApprove: () => void;
  onReject: () => void;
  onGenerate: () => void;
  onRequestChanges: (notes: string) => void;
  onAskQuestion: (question: string, ownerRole: string) => void;
}

const PieceDetail: React.FC<PieceDetailProps> = ({
  piece, initiativeId, busy, generating, error,
  onApprove, onReject, onGenerate, onRequestChanges, onAskQuestion,
}) => {
  const { theme } = useTheme();
  const { openTask } = useTaskModal();
  const decided = piece.status === 'approved' || piece.status === 'rejected' || piece.status === 'posted';
  const image = isImageType(piece.type);
  const hasDraft = !!(piece.draft && piece.draft.trim());
  const hasImage = !!(piece.image && (piece.image.url || piece.image.b64_file));
  const canApprove = piece.status === 'ready';
  // True while the async generate->critic->improve run is in flight.
  const inFlight = generating || isInFlight(piece.status);
  const genFailed = piece.status === 'error';
  // A piece in `drafting` has never been generated — the user must press Generate.
  const notGeneratedYet = !hasDraft && !hasImage && !inFlight && piece.status === 'drafting';
  const canRequestChanges = hasDraft && !decided && !image;

  // Elapsed-time ticker for the "Generating…" indicator.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!inFlight) { setElapsed(0); return; }
    const started = piece.gen_started_at ? new Date(piece.gen_started_at).getTime() : Date.now();
    const tick = () => setElapsed(Math.max(0, Math.round((Date.now() - started) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [inFlight, piece.gen_started_at]);
  const watchLiveTaskId = piece.task_id ?? null;

  // Request-changes form state.
  const [changeNotes, setChangeNotes] = useState('');
  const [showChangeForm, setShowChangeForm] = useState(false);

  // Ask-question form state.
  const [question, setQuestion] = useState('');
  const [questionRole, setQuestionRole] = useState<string>(QUESTION_ROLES[0]);
  const [showAskForm, setShowAskForm] = useState(false);

  // Live questions + answers for this piece.
  const [questions, setQuestions] = useState<PieceQuestionAnswer[]>([]);
  useEffect(() => {
    let cancelled = false;
    listPieceQuestions(initiativeId, piece.id)
      .then((qs) => { if (!cancelled) setQuestions(qs); })
      .catch((err) => console.error('Failed to load piece questions', err));
    return () => { cancelled = true; };
    // Reload when the piece changes or its question list updates.
  }, [initiativeId, piece.id, (piece.questions || []).length]);

  return (
    <div style={{
      backgroundColor: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: '8px',
      padding: '20px',
      minWidth: 0,
      overflowX: 'hidden',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <h2 style={{ color: theme.text, margin: 0, fontSize: '20px' }}>{pieceTitle(piece)}</h2>
          <div style={{ marginTop: '6px' }}>
            <StatusBadge status={piece.status} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={onGenerate}
            disabled={busy || inFlight || decided}
            title={decided ? `This piece is ${statusLabel(piece.status).toLowerCase()} — generation is locked.` : undefined}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: theme.primary,
              color: '#fff',
              cursor: busy || inFlight || decided ? 'not-allowed' : 'pointer',
              opacity: busy || inFlight || decided ? 0.6 : 1,
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            {inFlight ? 'Generating…' : genFailed ? 'Retry' : hasDraft ? 'Regenerate' : 'Generate'}
          </button>
          <button
            onClick={onApprove}
            disabled={busy || inFlight || !canApprove}
            title={canApprove ? undefined : 'Approve becomes available once the AI critic passes the piece (status: Ready).'}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#10b981',
              color: '#fff',
              cursor: busy || inFlight || !canApprove ? 'not-allowed' : 'pointer',
              opacity: busy || inFlight || !canApprove ? 0.5 : 1,
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            Approve
          </button>
          <button
            onClick={onReject}
            disabled={busy || inFlight || decided}
            title={decided ? `This piece is already ${statusLabel(piece.status).toLowerCase()}.` : undefined}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: `1px solid #ef4444`,
              backgroundColor: theme.surface,
              color: '#ef4444',
              cursor: busy || inFlight || decided ? 'not-allowed' : 'pointer',
              opacity: busy || inFlight || decided ? 0.5 : 1,
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            Reject
          </button>
          {canRequestChanges && (
            <button
              onClick={() => setShowChangeForm((v) => !v)}
              disabled={busy || inFlight}
              title="Tell the AI what to change; the next Generate will see your notes."
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: `1px solid ${theme.primary}`,
                backgroundColor: theme.surface,
                color: theme.primary,
                cursor: busy || inFlight ? 'not-allowed' : 'pointer',
                opacity: busy || inFlight ? 0.5 : 1,
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              Request changes
            </button>
          )}
          <button
            onClick={() => setShowAskForm((v) => !v)}
            disabled={busy || inFlight}
            title="Ask a teammate for a quote, fact, or approval you need to make this piece; their answer is used when generating it."
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.surface,
              color: theme.text,
              cursor: busy || inFlight ? 'not-allowed' : 'pointer',
              opacity: busy || inFlight ? 0.5 : 1,
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            Ask a teammate for input
          </button>
        </div>
      </div>

      {error && (
        <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '12px' }}>{error}</div>
      )}

      {/* Not-generated-yet prompt: make it obvious Generate is required. */}
      {notGeneratedYet && (
        <div style={{
          backgroundColor: theme.background,
          border: `1px dashed ${theme.primary}`,
          borderRadius: '6px',
          padding: '14px',
          marginBottom: '12px',
          color: theme.text,
          fontSize: '13px',
        }}>
          <strong>Not generated yet</strong> — press <strong>Generate</strong> to have the
          AI write the first draft. An AI critic then reviews it until it passes.
        </div>
      )}

      {/* Live generation progress (async): never appears stuck. */}
      {inFlight && (
        <div style={{
          backgroundColor: theme.background,
          border: `1px solid ${theme.primary}`,
          borderRadius: '6px',
          padding: '14px',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}>
          <span style={{
            width: '12px', height: '12px', borderRadius: '50%',
            border: `2px solid ${theme.primary}`, borderTopColor: 'transparent',
            display: 'inline-block', animation: 'init-spin 0.8s linear infinite',
          }} />
          <span style={{ color: theme.text, fontSize: '13px', fontWeight: 600 }}>
            Generating… (this can take a minute)
          </span>
          <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
            {elapsed}s elapsed — the AI is drafting and an AI critic is reviewing.
          </span>
          {watchLiveTaskId != null && (
            <button
              type="button"
              onClick={() => openTask(watchLiveTaskId)}
              style={{ background: 'none', border: 'none', padding: 0, color: theme.primary, fontSize: '13px', fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}
              title="Open the backing task to watch Claude's live output"
            >
              Watch live →
            </button>
          )}
          <style>{`@keyframes init-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Generation error state + Retry. */}
      {genFailed && (
        <div style={{
          backgroundColor: theme.background,
          border: `1px solid #ef4444`,
          borderRadius: '6px',
          padding: '14px',
          marginBottom: '12px',
        }}>
          <div style={{ color: '#ef4444', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
            Generation failed.
          </div>
          {piece.gen_error && (
            <div style={{ color: theme.textSecondary, fontSize: '12px', marginBottom: '8px', whiteSpace: 'pre-wrap' }}>
              {piece.gen_error}
            </div>
          )}
          <button
            onClick={onGenerate}
            disabled={busy || inFlight}
            style={{
              padding: '8px 16px', borderRadius: '6px', border: 'none',
              backgroundColor: theme.primary, color: '#fff', fontSize: '14px', fontWeight: 600,
              cursor: busy || inFlight ? 'not-allowed' : 'pointer',
              opacity: busy || inFlight ? 0.6 : 1,
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Request-changes form */}
      {showChangeForm && canRequestChanges && (
        <div style={{
          backgroundColor: theme.background,
          border: `1px solid ${theme.primary}`,
          borderRadius: '6px',
          padding: '14px',
          marginBottom: '12px',
        }}>
          <label style={{ display: 'block', color: theme.text, fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
            Request changes — what should the AI improve, and why?
          </label>
          <textarea
            value={changeNotes}
            onChange={(e) => setChangeNotes(e.target.value)}
            rows={3}
            placeholder="e.g. Open with the donor's pain point; cut the second paragraph; warmer tone."
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px', borderRadius: '6px',
              border: `1px solid ${theme.border}`, backgroundColor: theme.surface, color: theme.text,
              fontSize: '13px', resize: 'vertical', fontFamily: 'inherit',
            }}
          />
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button
              onClick={() => {
                if (!changeNotes.trim()) return;
                onRequestChanges(changeNotes.trim());
                setChangeNotes('');
                setShowChangeForm(false);
              }}
              disabled={busy || generating || !changeNotes.trim()}
              style={{
                padding: '8px 16px', borderRadius: '6px', border: 'none',
                backgroundColor: theme.primary, color: '#fff', fontSize: '14px', fontWeight: 600,
                cursor: busy || generating || !changeNotes.trim() ? 'not-allowed' : 'pointer',
                opacity: busy || generating || !changeNotes.trim() ? 0.6 : 1,
              }}
            >
              Submit & mark for revision
            </button>
            <span style={{ color: theme.textSecondary, fontSize: '12px', alignSelf: 'center' }}>
              Then press Generate to have the AI revise with your notes.
            </span>
          </div>
        </div>
      )}

      {/* Ask-a-question form */}
      {showAskForm && (
        <div style={{
          backgroundColor: theme.background,
          border: `1px solid ${theme.border}`,
          borderRadius: '6px',
          padding: '14px',
          marginBottom: '12px',
        }}>
          <label style={{ display: 'block', color: theme.text, fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
            Ask a teammate for input — a quote, a fact, or an approval needed to make this piece
          </label>
          <div style={{ color: theme.textSecondary, fontSize: '12px', marginBottom: '8px' }}>
            Sends a question to that person's Dependencies inbox; their answer is used when generating this content.
          </div>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={2}
            placeholder="e.g. What's the exact figure we raised last quarter?"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px', borderRadius: '6px',
              border: `1px solid ${theme.border}`, backgroundColor: theme.surface, color: theme.text,
              fontSize: '13px', resize: 'vertical', fontFamily: 'inherit', marginBottom: '8px',
            }}
          />
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              value={questionRole}
              onChange={(e) => setQuestionRole(e.target.value)}
              style={{
                padding: '8px 10px', borderRadius: '6px', border: `1px solid ${theme.border}`,
                backgroundColor: theme.surface, color: theme.text, fontSize: '13px',
                textTransform: 'capitalize', cursor: 'pointer',
              }}
            >
              {QUESTION_ROLES.map((r) => (
                <option key={r} value={r} style={{ textTransform: 'capitalize' }}>{r}</option>
              ))}
            </select>
            <button
              onClick={() => {
                if (!question.trim()) return;
                onAskQuestion(question.trim(), questionRole);
                setQuestion('');
                setShowAskForm(false);
              }}
              disabled={busy || generating || !question.trim()}
              style={{
                padding: '8px 16px', borderRadius: '6px', border: 'none',
                backgroundColor: theme.primary, color: '#fff', fontSize: '14px', fontWeight: 600,
                cursor: busy || generating || !question.trim() ? 'not-allowed' : 'pointer',
                opacity: busy || generating || !question.trim() ? 0.6 : 1,
              }}
            >
              Ask
            </button>
            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
              Creates a question in that person's Dependencies inbox. Their answer feeds the next generation.
            </span>
          </div>
        </div>
      )}

      {/* Questions + answers raised on this piece */}
      {questions.length > 0 && (
        <Section title="Input requested from teammates">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {questions.map((q, i) => (
              <div key={i} style={{
                backgroundColor: theme.background,
                border: `1px solid ${q.answer ? theme.success : theme.border}`,
                borderRadius: '6px',
                padding: '10px 12px',
              }}>
                <div style={{ color: theme.text, fontSize: '13px', fontWeight: 600 }}>
                  Q: {q.question}
                  {q.owner_role && (
                    <span style={{ color: theme.textSecondary, fontWeight: 400, fontSize: '12px' }}>
                      {' '}· to {q.owner_role}
                    </span>
                  )}
                </div>
                <div style={{ color: q.answer ? theme.text : theme.textSecondary, fontSize: '13px', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                  {q.answer ? `A: ${q.answer}` : 'Awaiting answer in the Dependencies inbox…'}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {piece.brief && piece.brief.trim() && (
        <Section title="Brief">
          <div style={{ color: theme.text, fontSize: '13px', whiteSpace: 'pre-wrap' }}>{piece.brief}</div>
        </Section>
      )}

      {/* Image pieces: show the generated image (or the prompt + Generate hint) */}
      {image ? (
        <>
          {piece.image_prompt && piece.image_prompt.trim() && (
            <Section title="Image Prompt">
              <div style={{ color: theme.text, fontSize: '13px', whiteSpace: 'pre-wrap' }}>{piece.image_prompt}</div>
            </Section>
          )}
          <Section title="Image">
            {hasImage ? (
              <img
                src={piece.image?.url
                  ? piece.image.url
                  : `${process.env.REACT_APP_API_URL || '/api'}/content/initiatives/${initiativeId}/pieces/${piece.id}/image`}
                alt={piece.image_prompt || 'Generated image'}
                style={{ maxWidth: '100%', borderRadius: '6px', border: `1px solid ${theme.border}` }}
              />
            ) : (
              <div style={{
                backgroundColor: theme.background, border: `1px dashed ${theme.border}`, borderRadius: '6px',
                padding: '20px', color: theme.textSecondary, fontSize: '14px', textAlign: 'center',
              }}>
                {inFlight
                  ? 'Generating the image via the image API…'
                  : 'No image yet. Press Generate above to create it from the prompt. If the image API isn’t configured, you’ll be pointed to Dependencies.'}
              </div>
            )}
          </Section>
        </>
      ) : !hasDraft ? (
        <Section title="Current Draft">
          <div style={{
            backgroundColor: theme.background,
            border: `1px dashed ${theme.border}`,
            borderRadius: '6px',
            padding: '20px',
            color: theme.textSecondary,
            fontSize: '14px',
            textAlign: 'center',
          }}>
            {inFlight
              ? 'Generating… the AI is drafting this piece and an AI critic is reviewing it. This updates automatically when it finishes.'
              : 'Not generated yet — press Generate above to have the AI write the first draft, then an AI critic reviews it.'}
          </div>
        </Section>
      ) : (
        <>
          <Section title="Current Draft">
            <pre style={{
              backgroundColor: theme.background,
              border: `1px solid ${theme.border}`,
              borderRadius: '6px',
              padding: '12px',
              color: theme.text,
              fontSize: '13px',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              maxHeight: '400px',
              overflowY: 'auto',
              margin: 0,
            }}>{piece.draft}</pre>
          </Section>

          <Section title={`Iteration Trail${typeof piece.iterations === 'number' ? ` (${piece.iterations})` : ''}`}>
            {(piece.review && piece.review.trim()) ? (
              <pre style={{
                backgroundColor: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                padding: '12px',
                color: theme.text,
                fontSize: '13px',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                maxHeight: '500px',
                overflowY: 'auto',
                margin: 0,
              }}>{piece.review}</pre>
            ) : (
              <div style={{ color: theme.textSecondary, fontSize: '14px' }}>
                No review trail yet. The generate→critic loop records each draft and critic verdict here.
              </div>
            )}
          </Section>
        </>
      )}

      {piece.reject_reason && piece.reject_reason.trim() && (
        <Section title="Rejection Reason">
          <div style={{ color: theme.text, fontSize: '13px', whiteSpace: 'pre-wrap' }}>{piece.reject_reason}</div>
        </Section>
      )}
    </div>
  );
};

interface ContentTabProps {
  selectedInitiative: InitiativeSummary;
  platforms: string[];
  allPlatforms: ContentPlatform[];
  pieces: PieceSummary[];
  piecesLoading: boolean;
  piecesError: string | null;
  piece: Piece | null;
  pieceLoading: boolean;
  pieceError: string | null;
  pieceIdParam: string | null;
  actionBusy: boolean;
  generating: boolean;
  formPlatform: string;
  formCustomPlatform: string;
  formType: string;
  formBrief: string;
  formImagePrompt: string;
  creating: boolean;
  createError: string | null;
  onPlatformChange: (v: string) => void;
  onCustomPlatformChange: (v: string) => void;
  onTypeChange: (v: string) => void;
  onBriefChange: (v: string) => void;
  onImagePromptChange: (v: string) => void;
  onCreatePiece: () => void;
  onSelectPiece: (p: PieceSummary) => void;
  onApprove: () => void;
  onReject: () => void;
  onGenerate: () => void;
  onRequestChanges: (notes: string) => void;
  onAskQuestion: (question: string, ownerRole: string) => void;
}

const ContentTab: React.FC<ContentTabProps> = ({
  selectedInitiative,
  platforms,
  allPlatforms,
  pieces,
  piecesLoading,
  piecesError,
  piece,
  pieceLoading,
  pieceError,
  pieceIdParam,
  actionBusy,
  generating,
  formPlatform,
  formCustomPlatform,
  formType,
  formBrief,
  formImagePrompt,
  creating,
  createError,
  onPlatformChange,
  onCustomPlatformChange,
  onTypeChange,
  onBriefChange,
  onImagePromptChange,
  onCreatePiece,
  onSelectPiece,
  onApprove,
  onReject,
  onGenerate,
  onRequestChanges,
  onAskQuestion,
}) => {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* The Content Guidelines apply to every piece generated below. */}
      <ContentGuidelinesEditor initiativeId={selectedInitiative.id} />

      <div style={{
        backgroundColor: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: '8px',
        padding: '20px',
      }}>
        <h2 style={{ color: theme.text, margin: '0 0 6px', fontSize: '18px' }}>
          {initiativeTitle(selectedInitiative)} — Pieces
        </h2>
        <p style={{ color: theme.textSecondary, margin: '0 0 16px', fontSize: '13px' }}>
          Create a piece (text, video script, or image). The AI drafts and an AI critic reviews
          text until it passes; request changes or ask a question to steer it; then you approve before posting.
        </p>

        <NewPieceForm
          platforms={platforms}
          allPlatforms={allPlatforms}
          platform={formPlatform}
          customPlatform={formCustomPlatform}
          pieceType={formType}
          brief={formBrief}
          imagePrompt={formImagePrompt}
          busy={creating}
          error={createError}
          onPlatformChange={onPlatformChange}
          onCustomPlatformChange={onCustomPlatformChange}
          onTypeChange={onTypeChange}
          onBriefChange={onBriefChange}
          onImagePromptChange={onImagePromptChange}
          onSubmit={onCreatePiece}
        />

        {piecesLoading ? (
          <div style={{ color: theme.textSecondary, fontSize: '14px' }}>Loading pieces…</div>
        ) : piecesError ? (
          <div style={{ color: '#ef4444', fontSize: '14px' }}>{piecesError}</div>
        ) : pieces.length === 0 ? (
          <div style={{ color: theme.textSecondary, fontSize: '14px' }}>
            No pieces yet. When a piece is created and generated, the AI draft and critic
            review trail will appear here for your approval.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {pieces.map((p) => (
              <div
                key={p.id}
                onClick={() => onSelectPiece(p)}
                style={{
                  padding: '12px',
                  backgroundColor: pieceIdParam === p.id ? theme.surfaceHover : theme.background,
                  border: `1px solid ${pieceIdParam === p.id ? statusColor(p.status) : theme.border}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ color: theme.text, fontSize: '14px', fontWeight: 600 }}>{pieceTitle(p)}</span>
                <StatusBadge status={p.status} />
              </div>
            ))}
          </div>
        )}
      </div>

      {pieceIdParam && (
        pieceLoading ? (
          <PanelMessage>Loading piece…</PanelMessage>
        ) : pieceError && !piece ? (
          <PanelMessage tone="error">{pieceError}</PanelMessage>
        ) : piece ? (
          <PieceDetail
            piece={piece}
            initiativeId={selectedInitiative.id}
            busy={actionBusy}
            generating={generating}
            error={pieceError}
            onApprove={onApprove}
            onReject={onReject}
            onGenerate={onGenerate}
            onRequestChanges={onRequestChanges}
            onAskQuestion={onAskQuestion}
          />
        ) : null
      )}
    </div>
  );
};

export default Initiatives;
